import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { TextMultipleLangItem } from '@adornis/buchungsheld-db/entities/TextMultipleLangItem.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { useGeneratedForm } from '@adornis/generated-form/use-generated-form.js';
import { html } from 'lit';
import * as m from '../../../../localization/out/messages.js';
import { subscribeMatchingWeekSettings } from '../../../api/settings.js';
import { MatchingWeekSettings } from '../../../db/settings.js';
import { formatFormError } from '../../components/format-form-error.js';
import { MultilanguageFormattableTextarea } from '../../components/multilanguage-formattable-textarea.js';

export const AdminEditImprint = stateful(() => {
  const [saving, setSaving] = useState(false, []);
  const [saveSuccess, setSaveSuccess] = useState(false, []);
  const [saveError, setSaveError] = useState<string | null>(null, []);

  const settingsQuery = useQuery(
    () => subscribeMatchingWeekSettings()({ imprint: selectionSet(() => TextMultipleLangItem, 1) }),
    [],
  );

  const [placeholder] = useState(new MatchingWeekSettings({}));
  const settings = (settingsQuery.value ?? placeholder) as MatchingWeekSettings;
  const { form, renderField } = useGeneratedForm<MatchingWeekSettings>(
    settings,
    {
      translationPrefix: 'imprint_',
      validator: null,
    },
    [settings],
  );

  const handleSave = async () => {
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(null);
    try {
      const err = await form.validate();
      if (err) {
        setSaveError(formatFormError(err, 'imprint_'));
        return;
      }
      await settings.save();
      setSaveSuccess(true);
    } catch (e: any) {
      console.error(e);
      setSaveError(formatFormError(e, 'imprint_'));
    } finally {
      setSaving(false);
    }
  };

  return html`
    <div class="p-4 sm:p-8 flex flex-col items-center">
      <div class="max-w-4xl w-full">
        ${
          settingsQuery.loading
            ? html`
                <div class="card bg-base-100 shadow-xl">
                  <div class="card-body items-center justify-center">
                    <span class="loading loading-spinner"></span>
                    <span>${m.admin_legal_loading_settings()}</span>
                  </div>
                </div>
              `
            : ''
        }
        ${
          settingsQuery.error
            ? html`
                <div class="alert alert-error mb-4">
                  <span class="icon-[material-symbols--error]"></span>
                  <span>${settingsQuery.error.message ?? m.admin_legal_load_failed()}</span>
                </div>
              `
            : ''
        }
        ${
          settingsQuery.value
            ? html`
                <div class="card bg-base-100 shadow-xl ${saving ? 'opacity-70 pointer-events-none' : ''}">
                  <div class="card-body">
                    <h2 class="card-title text-2xl mb-4">${m.admin_edit_imprint_title()}</h2>
                    ${
                      saveSuccess
                        ? html`<div class="alert alert-success">
                            <span class="icon-[material-symbols--check-circle]"></span
                            ><span>${m.admin_legal_save_success()}</span>
                          </div>`
                        : ''
                    }
                    ${
                      saveError
                        ? html`<div class="alert alert-error">
                            <span class="icon-[material-symbols--error]"></span><span>${saveError}</span>
                          </div>`
                        : ''
                    }
                    ${renderField(['imprint'], MultilanguageFormattableTextarea)}
                  </div>
                </div>
              `
            : ''
        }
        <div class="flex justify-end gap-4 mt-4">
          <button
            class=${`btn btn-primary ${saving ? 'loading' : ''}`}
            ?disabled=${saving || settingsQuery.loading}
            @click=${handleSave}
          >
            <span class="icon-[material-symbols--save]"></span>
            ${m.save()}
          </button>
        </div>
      </div>
    </div>
  `;
});
